import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  CircleStackIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import Button from '../ui/Button';
import ConfirmDialog from '../ui/ConfirmDialog';
import Switch from '../ui/Switch';

const CONFIRM_PHRASE = 'RESTORE';

function formatBytes(value) {
  const bytes = Number(value) || 0;
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let size = bytes / 1024;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[unit]}`;
}

function StatCard({ label, value, accent }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-premium p-4"
    >
      <p className="text-sm text-secondary-500 ">{label}</p>
      <p className={`mt-1 text-3xl font-bold ${accent || 'text-secondary-900 '}`}>{value}</p>
    </motion.div>
  );
}

function PlatformBackupsView() {
  const { t } = useLanguage();
  const b = t.platformAdmin?.backups || {};

  const [info, setInfo] = useState(null);
  const [infoLoading, setInfoLoading] = useState(true);
  const [infoError, setInfoError] = useState(false);
  const [includeFiles, setIncludeFiles] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const loadInfo = useCallback(async () => {
    setInfoLoading(true);
    setInfoError(false);
    try {
      const response = await axios.get('/api/admin/backups/info', { withCredentials: true });
      setInfo(response.data);
    } catch (error) {
      setInfoError(true);
      toast.error(error.response?.data?.error || b.loadError || 'Could not load backup inventory');
    } finally {
      setInfoLoading(false);
    }
  }, [b.loadError]);

  useEffect(() => {
    void loadInfo();
  }, [loadInfo]);

  const tables = info?.tables || {};
  const tableCount = Object.keys(tables).length;
  const totalRows = info?.total_rows ?? 0;
  const fileStats = info?.files || { total: 0, total_bytes: 0 };

  const handleDownload = async () => {
    setDownloading(true);
    setResult(null);
    try {
      const response = await axios.get('/api/admin/backups/download', {
        withCredentials: true,
        responseType: 'blob',
        params: { include_files: includeFiles ? 1 : 0 },
      });
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `enrola-erp-backup-${stamp}.zip`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      toast.success(b.downloadSuccess || 'Backup downloaded');
    } catch (error) {
      let message = error.response?.data?.error || b.downloadError || 'Could not generate the backup';
      if (error.response?.data instanceof Blob) {
        try {
          const parsed = JSON.parse(await error.response.data.text());
          message = parsed.error || message;
        } catch (parseError) {
          // keep default message
        }
      }
      toast.error(message);
    } finally {
      setDownloading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setResult(null);
  };

  const canRestore = Boolean(selectedFile) && confirmText.trim().toUpperCase() === CONFIRM_PHRASE && !restoring;

  const handleRestore = async () => {
    if (!canRestore) return;
    setShowConfirm(false);
    setRestoring(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', selectedFile);
      form.append('confirm', CONFIRM_PHRASE);
      const response = await axios.post('/api/admin/backups/restore', form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(response.data);
      setSelectedFile(null);
      setConfirmText('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      toast.success(b.restoreSuccess || 'Backup restored');
      void loadInfo();
    } catch (error) {
      toast.error(error.response?.data?.error || b.restoreError || 'The restore failed');
    } finally {
      setRestoring(false);
    }
  };

  if (infoLoading) {
    return (
      <div className="space-y-4">
        <div className="h-32 animate-pulse rounded-2xl bg-secondary-100 " />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[0, 1, 2].map((index) => (
            <div key={index} className="h-28 animate-pulse rounded-2xl bg-secondary-100 " />
          ))}
        </div>
        <div className="h-64 animate-pulse rounded-2xl bg-secondary-100 " />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-premium overflow-hidden"
      >
        <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-glow">
            <CircleStackIcon className="h-7 w-7" />
          </div>
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-secondary-900 ">{b.heroTitle || 'Full platform backups'}</h2>
            <p className="mt-1 text-sm text-secondary-600 ">{b.heroDescription}</p>
            <p className="mt-2 text-xs font-medium text-secondary-500 ">{b.formatNote}</p>
          </div>
        </div>
      </motion.div>

      {infoError ? (
        <div className="rounded-2xl border border-dashed border-secondary-300 p-10 text-center ">
          <p className="text-secondary-600 ">{b.loadError}</p>
          <button
            type="button"
            onClick={() => void loadInfo()}
            className="mt-4 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
          >
            {b.retry || 'Try again'}
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label={b.statTables || 'Tables'} value={tableCount} />
            <StatCard label={b.statRows || 'Data rows'} value={totalRows.toLocaleString()} accent="text-orange-600 " />
            <StatCard label={b.statFiles || 'Stored files'} value={fileStats.total.toLocaleString()} />
            <StatCard label={b.statFileSize || 'File storage'} value={formatBytes(fileStats.total_bytes)} />
          </div>

          {result ? (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-premium border border-success-200/80 bg-success-50/90 p-5"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-success-500 text-white">
                  <CheckCircleIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-success-900 ">{b.restoreResultTitle || 'Restore complete'}</p>
                  <p className="mt-1 text-sm text-success-800/90 ">
                    {b.restoreResultDescription || 'The data from your backup file is now live.'}
                  </p>
                  <ul className="mt-2 space-y-0.5 text-sm text-success-800/90 ">
                    <li>{b.restoreResultTables || 'Tables restored'}: {(result.database?.tables ? Object.keys(result.database.tables).length : 0)}</li>
                    <li>{b.restoreResultFiles || 'Files restored'}: {result.files_restored ?? 0}</li>
                    {result.manifest?.created_at ? (
                      <li>{b.restoreResultSource || 'Backup created'}: {new Date(result.manifest.created_at).toLocaleString()}</li>
                    ) : null}
                  </ul>
                  <div className="mt-3">
                    <Button type="button" variant="secondary" size="compact" onClick={() => window.location.reload()}>
                      {b.reload || 'Reload page'}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : null}

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="card-premium flex flex-col p-6">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-500/10 text-orange-600 ">
                  <ArrowDownTrayIcon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900 ">{b.downloadTitle || 'Download backup'}</h3>
                  <p className="text-sm text-secondary-500 ">{b.downloadDescription}</p>
                </div>
              </div>

              <div className="mt-5 flex-1 rounded-2xl border border-secondary-200/80 bg-secondary-50/60 p-4 ">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-secondary-800 ">{b.includeFiles || 'Include uploaded files'}</p>
                    <p className="mt-1 text-xs text-secondary-500 ">{b.includeFilesHint}</p>
                  </div>
                  <Switch checked={includeFiles} onChange={setIncludeFiles} aria-label={b.includeFiles || 'Include uploaded files'} />
                </div>
                <p className="mt-3 text-xs text-secondary-500 ">
                  {b.inventorySummary
                    ? b.inventorySummary
                        .replace('{rows}', totalRows.toLocaleString())
                        .replace('{files}', fileStats.total.toLocaleString())
                    : `${totalRows.toLocaleString()} rows`}
                </p>
              </div>

              <div className="mt-5">
                <Button type="button" variant="primary" icon={ArrowDownTrayIcon} loading={downloading} onClick={handleDownload}>
                  {downloading ? (b.downloading || 'Preparing backup...') : (b.downloadButton || 'Download backup')}
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-premium flex flex-col border border-error-200/70 p-6"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-error-500/10 text-error-600 ">
                  <ArrowUpTrayIcon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900 ">{b.restoreTitle || 'Restore from backup'}</h3>
                  <p className="text-sm text-secondary-500 ">{b.restoreDescription}</p>
                </div>
              </div>

              <div className="mt-5 rounded-2xl border border-error-200/70 bg-error-50/70 p-4 ">
                <div className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-error-600 " />
                  <div>
                    <p className="text-sm font-semibold text-error-800 ">{b.warningTitle || 'This replaces all current data'}</p>
                    <p className="mt-1 text-xs text-error-700/90 ">{b.warningText}</p>
                  </div>
                </div>
              </div>

              <div className="mt-5 space-y-4">
                <div>
                  <label className="form-label" htmlFor="backup-file">{b.chooseFile || 'Backup file (.zip)'}</label>
                  <input
                    id="backup-file"
                    ref={fileInputRef}
                    type="file"
                    accept=".zip,application/zip"
                    onChange={handleFileChange}
                    className="block w-full cursor-pointer rounded-xl border border-secondary-300 bg-white px-3 py-2.5 text-sm text-secondary-700 file:mr-3 file:rounded-lg file:border-0 file:bg-orange-500 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-white hover:file:bg-orange-600  "
                  />
                  {selectedFile ? (
                    <p className="mt-2 text-xs text-secondary-500 ">
                      {selectedFile.name} · {formatBytes(selectedFile.size)}
                    </p>
                  ) : (
                    <p className="mt-2 text-xs text-secondary-500 ">{b.noFileSelected}</p>
                  )}
                </div>

                <div>
                  <label className="form-label" htmlFor="backup-confirm">{b.confirmLabel || `Type ${CONFIRM_PHRASE} to confirm`}</label>
                  <input
                    id="backup-confirm"
                    type="text"
                    value={confirmText}
                    onChange={(event) => setConfirmText(event.target.value)}
                    placeholder={CONFIRM_PHRASE}
                    autoComplete="off"
                    className="block w-full rounded-xl border border-secondary-300 bg-white px-3 py-2.5 text-sm text-secondary-900 placeholder:text-secondary-400  "
                  />
                </div>
              </div>

              <div className="mt-5">
                <Button
                  type="button"
                  variant="danger"
                  icon={ShieldExclamationIcon}
                  disabled={!canRestore}
                  loading={restoring}
                  onClick={() => setShowConfirm(true)}
                >
                  {restoring ? (b.restoring || 'Restoring...') : (b.restoreButton || 'Restore backup')}
                </Button>
              </div>
            </motion.div>
          </div>
        </>
      )}

      <ConfirmDialog
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleRestore}
        title={b.restoreConfirmTitle || 'Restore this backup?'}
        message={b.restoreConfirmMessage}
        confirmLabel={b.restoreConfirmAction || 'Yes, restore'}
        cancelLabel={t.common?.cancel || 'Cancel'}
        confirmVariant="danger"
        loading={restoring}
      />
    </div>
  );
}

export default PlatformBackupsView;
